import { z } from "zod";
import { VehicleCondition } from "../codecs/schema/vehicle";
import { FinanceType, TransactionSource, TransactionStatusSchema } from "./tdc";

export const nullableObject = <TSchema extends z.AnyZodObject>(
  schema: TSchema
) => {
  const entries = Object.entries(schema.shape) as [
    keyof TSchema["shape"],
    z.ZodTypeAny,
  ][];

  const newProps = entries.reduce(
    (acc, [key, value]) => {
      acc[key] = value.nullable();
      return acc;
    },
    {} as {
      [key in keyof TSchema["shape"]]: z.ZodNullable<TSchema["shape"][key]>;
    }
  );

  return z.object(newProps);
};

// Statuses initial values
export const getReadableTransactionStatus = (
  status: TransactionStatusSchema
) => {
  if (!status) return "-";
  const map = {
    REGISTERED: "Registered",
    SIGNED_UP: "Signed Up",
    IN_PROGRESS: "In Progress",

    PREQUAL_SUBMITTED: "Prequal Submitted",
    CREDIT_APP_SUBMITTED: "Credit App Submitted",
    FILE_COMPLETE: "Ready for Signatures", // The label is intentionally different than the enum
    READY_FOR_FNI: "Ready for F&I",

    DELIVERED: "Delivered",

    // Deprecated
    WEB_LEAD: "Web Lead",
    WEB_APPLICATION: "Web Application",
    DISASSOCIATED: "Dissociated",
    COMPLETED: "Completed",
    NOT_STARTED: "Not Started",
    DEAD: "Dead",
    RULE_90_DAYS: "Locked after 90 days",
  };
  return map[status] ?? "-";
};

export const getHumanReadableTransactionSource = (
  source: TransactionSource
) => {
  const map: { [key in TransactionSource]: string } = {
    WEB_PREQUAL: "Web-Lead",
    WEB_APPLICATION: "Web-App",
    KIOSK: "Kiosk",
    ADMIN_DASHBOARD: "Dashboard",
  };

  return map[source];
};

export const getReadableTestScenarioName = (scenarioType: string) => {
  const map: { [key in string]: string } = {
    SCENARIO1: "Scenario 1 - Consumer Kiosk Registration",
    SCENARIO2: "Scenario 2 - Admin Dashboard Registration",
    SCENARIO3: "Scenario 3 - Web Prequal Registration",
  };

  return map[scenarioType];
};

export const getHumanReadableVehicleCondition = (
  condition: VehicleCondition
) => {
  const map: { [key in VehicleCondition]: string } = {
    EXCELLENT: "Excellent",
    GOOD: "Good",
    SATISFACTORY: "Satisfactory",
    UNSATISFACTORY: "Unsatisfactory",
  };

  return map[condition];
};

export const getHumanReadableFinanceType = (financeType: FinanceType) => {
  const map: { [key in FinanceType]: string } = {
    CASH: "Cash",
    FINANCE: "Finance",
    LEASE: "Lease",
    OUTSIDE_FINANCING: "Outside Financing",
    UNDECIDED: "Undecided",
  };

  return map[financeType];
};
